import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import {
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  FormRow,
  Divider,
  Check,
  X,
  ArrowLeft,
  ArrowRight,
  ConfirmDialog,
  ModalShell,
  Tile,
} from "@/components/ds";

// Backend contract: server/Velaro.Messaging/Controllers/Voice/TelephonyProvisioningController.cs
// GET/POST/DELETE TelephonyProvisioning, site-scoped via auth context, one record per site.

export interface ProvisioningRecord {
  id?: number;
  siteId?: number;
  providerSlug?: string;
  status: "Pending" | "Provisioning" | "Provisioned" | "Failed" | "Deprovisioned" | string;
  provisioned: boolean;
  externalAccountId?: string | null;
  externalExtensionId?: string | null;
  provisionedNumber?: string | null;
  errorMessage?: string | null;
  requestedAtUtc?: string | null;
  provisionedAtUtc?: string | null;
  deprovisionedAtUtc?: string | null;
  updatedAtUtc?: string | null;
}

type Step = "connect" | "verify" | "configure" | "finish";
const STEPS: Step[] = ["connect", "verify", "configure", "finish"];
const STEP_LABELS: Record<Step, string> = {
  connect: "Connect",
  verify: "Verify",
  configure: "Configure",
  finish: "Finish",
};

type Mode = "loading" | "error" | "manage" | "wizard";

// Step indicator, pattern copied from IvrSetupWizard.tsx's StepBar per CLAUDE.md
// Wizard Design Standard (canonical 4-phase shell, not StarterWorkflowWizard).
function StepBar({ current }: { current: Step }) {
  const idx = STEPS.indexOf(current);
  return (
    <div className="flex items-center gap-0 mb-8" role="list" aria-label="Setup progress">
      {STEPS.map((s, i) => {
        const done = i < idx;
        const active = s === current;
        return (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center gap-1" role="listitem">
              <div
                aria-current={active ? "step" : undefined}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors ${
                  done
                    ? "bg-[var(--color-primary)] border-[var(--color-primary)] text-white"
                    : active
                    ? "border-[var(--color-primary)] text-[var(--color-primary)] bg-white"
                    : "border-zinc-300 text-zinc-400 bg-white"
                }`}
              >
                {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className={`text-xs font-medium ${active ? "text-[var(--color-primary)]" : done ? "text-zinc-600" : "text-zinc-400"}`}>
                {STEP_LABELS[s]}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mb-4 mx-1 ${i < idx ? "bg-[var(--color-primary)]" : "bg-zinc-200"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    Provisioned: "bg-green-100 text-green-800",
    Provisioning: "bg-blue-100 text-blue-800",
    Pending: "bg-zinc-100 text-zinc-600",
    Failed: "bg-red-100 text-red-700",
    Deprovisioned: "bg-zinc-100 text-zinc-500",
  };
  return (
    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${map[status] ?? "bg-zinc-100 text-zinc-600"}`}>
      {status}
    </span>
  );
}

interface Props {
  /** Default site display name to pre-fill the Connect step (e.g. the current site's name). */
  defaultSiteName?: string;
  onClose(): void;
  /** Called any time the provisioning record changes (provisioned or deprovisioned) so the parent page can refresh. */
  onChanged?(record: ProvisioningRecord | null): void;
}

export default function VelaroVoiceSetupWizard({ defaultSiteName = "", onClose, onChanged }: Props) {
  const api = useApi();

  const [mode, setMode] = useState<Mode>("loading");
  const [record, setRecord] = useState<ProvisioningRecord | null>(null);
  const [loadError, setLoadError] = useState("");

  const [step, setStep] = useState<Step>("connect");
  const [stepError, setStepError] = useState("");

  // Connect step fields
  const [siteName, setSiteName] = useState(defaultSiteName);
  const [contactEmail, setContactEmail] = useState("");
  const [areaCodePreference, setAreaCodePreference] = useState("");

  // Configure step fields
  const [extensionNumber, setExtensionNumber] = useState("");

  // Submission state (Finish step)
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitResult, setSubmitResult] = useState<ProvisioningRecord | null>(null);

  // Manage-screen "start over" (deprovision) state
  const [confirmStartOver, setConfirmStartOver] = useState(false);
  const [deprovisioning, setDeprovisioning] = useState(false);
  const [manageError, setManageError] = useState("");

  // Load existing provisioning state. Re-entrancy: never silently re-run Connect.
  const fetchStatus = useCallback(() => {
    setMode("loading");
    setLoadError("");
    return api.messaging
      .get("TelephonyProvisioning")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d: ProvisioningRecord) => {
        setRecord(d);
        if (d.provisioned || d.status === "Provisioning") {
          setMode("manage");
        } else {
          setMode("wizard");
          setStep("connect");
        }
      })
      .catch((err) => {
        setLoadError(
          `Failed to load Velaro Voice status: ${err instanceof Error ? err.message : String(err)}`
        );
        setMode("error");
      });
  }, [api.messaging]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Navigation
  function goNext() {
    setStepError("");
    if (step === "connect") {
      if (!siteName.trim()) { setStepError("Business/site name is required."); return; }
      if (!contactEmail.trim()) { setStepError("A contact email is required so we can reach you about this line."); return; }
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim());
      if (!emailOk) { setStepError("Enter a valid email address."); return; }
      setStep("verify");
      return;
    }
    if (step === "verify") {
      setStep("configure");
      return;
    }
    if (step === "configure") {
      setStep("finish");
      return;
    }
  }

  function goBack() {
    setStepError("");
    if (step === "verify") setStep("connect");
    else if (step === "configure") setStep("verify");
    else if (step === "finish" && !submitting && !submitResult) setStep("configure");
  }

  // Submit provisioning request (fires when entering Finish)
  const doProvision = useCallback(async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const body = {
        siteName: siteName.trim(),
        contactEmail: contactEmail.trim() || null,
        areaCodePreference: areaCodePreference.trim() || null,
        extensionNumber: extensionNumber.trim() ? Number(extensionNumber.trim()) : null,
      };
      const resp = await api.messaging.post("TelephonyProvisioning", body);
      const data = await resp.json().catch(() => null);
      if (!resp.ok) {
        setSubmitError(
          data?.error ?? `Setup failed (HTTP ${resp.status}). You can try again, or contact support@velaro.com.`
        );
        if (data) { setRecord(data); setSubmitResult(data); }
        return;
      }
      setSubmitResult(data);
      setRecord(data);
      onChanged?.(data);
    } catch (ex) {
      setSubmitError(`An unexpected error occurred: ${ex instanceof Error ? ex.message : String(ex)}`);
    } finally {
      setSubmitting(false);
    }
  }, [api.messaging, siteName, contactEmail, areaCodePreference, extensionNumber, onChanged]);

  useEffect(() => {
    if (step === "finish" && !submitting && !submitResult && !submitError) {
      doProvision();
    }
  }, [step, submitting, submitResult, submitError, doProvision]);

  function retrySubmit() {
    setSubmitError("");
    setSubmitResult(null);
    doProvision();
  }

  // Start over (deprovision) from the Manage screen
  async function handleStartOver() {
    setDeprovisioning(true);
    setManageError("");
    try {
      const resp = await api.messaging.delete("TelephonyProvisioning");
      const data = await resp.json().catch(() => null);
      if (!resp.ok) {
        setManageError(data?.error ?? `Failed to disconnect (HTTP ${resp.status}).`);
        return;
      }
      setConfirmStartOver(false);
      onChanged?.(data ?? null);
      setRecord(data ?? null);
      setSubmitResult(null);
      setSubmitError("");
      setSiteName(defaultSiteName);
      setContactEmail("");
      setAreaCodePreference("");
      setExtensionNumber("");
      setStep("connect");
      setMode("wizard");
    } catch (ex) {
      setManageError(`An unexpected error occurred: ${ex instanceof Error ? ex.message : String(ex)}`);
    } finally {
      setDeprovisioning(false);
    }
  }

  // Render: Manage screen (existing connected/in-progress line)
  function ManageScreen() {
    if (!record) return null;
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold text-zinc-800">Velaro Voice</h2>
          <StatusPill status={record.status} />
        </div>

        {record.status === "Provisioning" ? (
          <Tile density="comfortable">
            <Tile.Leading><LoadingSpinner inline size={20} /></Tile.Leading>
            <Tile.Body>
              <p className="text-sm text-zinc-600">
                Your Velaro Voice account is being set up. This usually completes within a few minutes.
              </p>
            </Tile.Body>
          </Tile>
        ) : (
          <div className="bg-zinc-50 rounded-xl p-5 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Phone number</span>
              <span className="font-medium text-zinc-800">{record.provisionedNumber || "Not assigned"}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Connected since</span>
              <span className="font-medium text-zinc-800">
                {record.provisionedAtUtc ? new Date(record.provisionedAtUtc).toLocaleString() : "Not available"}
              </span>
            </div>
          </div>
        )}

        {manageError && (
          <div role="alert" className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {manageError}
          </div>
        )}

        <Divider />

        <div className="flex items-center justify-between">
          <GhostButton onClick={() => setConfirmStartOver(true)} className="!text-red-600 hover:!text-red-700">
            Start over
          </GhostButton>
          <PrimaryButton onClick={() => fetchStatus()}>Refresh status</PrimaryButton>
        </div>

        <ConfirmDialog
          open={confirmStartOver}
          title="Start over with Velaro Voice?"
          message="This disconnects your current Velaro Voice number and account. Any active extensions stop working until setup is completed again. This cannot be undone."
          confirmLabel="Disconnect and start over"
          variant="danger"
          busy={deprovisioning}
          onConfirm={handleStartOver}
          onCancel={() => setConfirmStartOver(false)}
        />
      </div>
    );
  }

  // Render: wizard steps
  function StepConnect() {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-zinc-800 mb-1">Connect Velaro Voice</h2>
          <p className="text-sm text-zinc-500">
            We'll set up a dedicated phone number and account for your business. No hardware or carrier
            account is needed. Velaro handles it end to end.
          </p>
        </div>

        {record?.status === "Failed" && record.errorMessage && (
          <div role="alert" className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            Your last setup attempt failed: {record.errorMessage}. You can try again below.
          </div>
        )}

        <FormRow label="Business / site name" hint="Shown on your Velaro Voice account">
          <input
            id="velaro-phone-site-name"
            className="border border-zinc-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-[var(--color-primary)]"
            value={siteName}
            onChange={(e) => setSiteName(e.target.value)}
            placeholder="e.g. Acme Plumbing"
            aria-label="Business or site name"
          />
        </FormRow>

        <FormRow label="Contact email" hint="Where we'll send setup and billing notices">
          <input
            id="velaro-phone-contact-email"
            type="email"
            className="border border-zinc-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-[var(--color-primary)]"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="you@yourcompany.com"
            aria-label="Contact email"
          />
        </FormRow>

        <FormRow label="Preferred area code" hint="Optional. We'll assign the closest available number if left blank">
          <input
            id="velaro-phone-area-code"
            className="border border-zinc-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-[var(--color-primary)]"
            value={areaCodePreference}
            onChange={(e) => setAreaCodePreference(e.target.value)}
            placeholder="e.g. 415"
            maxLength={3}
            aria-label="Preferred area code"
          />
        </FormRow>
      </div>
    );
  }

  function StepVerify() {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-zinc-800 mb-1">Verify your details</h2>
          <p className="text-sm text-zinc-500">Double-check everything before we create your account.</p>
        </div>

        <div className="bg-zinc-50 rounded-xl p-5 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Business / site name</span>
            <span className="font-medium text-zinc-800">{siteName || "Not set"}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Contact email</span>
            <span className="font-medium text-zinc-800">{contactEmail || "Not set"}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Preferred area code</span>
            <span className="font-medium text-zinc-800">{areaCodePreference || "Any available"}</span>
          </div>
        </div>

        <p className="text-xs text-zinc-400">
          Need to change something? Go back to the previous step.
        </p>
      </div>
    );
  }

  function StepConfigure() {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-zinc-800 mb-1">Configure your first extension</h2>
          <p className="text-sm text-zinc-500">
            Optional. Assign a starting extension for this account. You can add more extensions later.
          </p>
        </div>

        <FormRow label="Extension number" hint="Optional. Leave blank to add extensions later">
          <input
            id="velaro-phone-extension"
            type="number"
            className="border border-zinc-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-[var(--color-primary)]"
            value={extensionNumber}
            onChange={(e) => setExtensionNumber(e.target.value)}
            placeholder="e.g. 101"
            aria-label="Extension number"
          />
        </FormRow>
      </div>
    );
  }

  function StepFinish() {
    if (submitting) {
      return (
        <div className="text-center space-y-4 py-8">
          <LoadingSpinner inline size={36} />
          <p className="text-sm text-zinc-500">Creating your Velaro Voice account...</p>
        </div>
      );
    }

    if (submitError) {
      return (
        <div className="space-y-6 py-2">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
            <span className="text-red-600 text-2xl" aria-hidden="true">!</span>
          </div>
          <div className="text-center">
            <h2 className="text-xl font-semibold text-zinc-800 mb-2">Setup failed</h2>
            <p className="text-sm text-zinc-500 max-w-md mx-auto">{submitError}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-2">
            <PrimaryButton onClick={retrySubmit}>Try again</PrimaryButton>
            <SecondaryButton onClick={onClose}>Close</SecondaryButton>
          </div>
        </div>
      );
    }

    return (
      <div className="text-center space-y-6 py-4">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-zinc-800 mb-2">Velaro Voice is connected!</h2>
          <p className="text-sm text-zinc-500 max-w-md mx-auto">
            <strong>{siteName}</strong> is ready to take calls.
          </p>
        </div>

        <div className="bg-zinc-50 rounded-xl p-5 text-left space-y-3 max-w-sm mx-auto">
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Phone number</span>
            <span className="font-medium text-zinc-800">{submitResult?.provisionedNumber || "Assigning..."}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Extension</span>
            <span className="font-medium text-zinc-800">{extensionNumber || "Not set"}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Status</span>
            <span className="font-medium text-zinc-800">
              <StatusPill status={submitResult?.status ?? "Provisioned"} />
            </span>
          </div>
        </div>

        <PrimaryButton onClick={onClose}>Done</PrimaryButton>
      </div>
    );
  }

  // Layout
  const showStepBar = mode === "wizard" && step !== "finish";
  const showFooter = mode === "wizard" && step !== "finish";

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      {/* No separate backdrop div here — headlessui's <Dialog> already renders
          its own backdrop/overlay; a second hand-rolled "fixed inset-0 bg-black"
          div would be pure duplication (same pattern as TriggerModal.tsx). */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        {/* DialogPanel is the headlessui focus-trap boundary; ModalShell (ds/) supplies
            the actual card shell so this stays a single ds-approved wrapper instead of a
            hand-rolled "rounded-2xl bg-white shadow-2xl" div. */}
        <DialogPanel>
        <ModalShell size="xl" height="tall" className="mx-4">
          <div className="px-8 pt-8 pb-0">
            <div className="flex items-center justify-between mb-6">
              <DialogTitle className="text-lg font-semibold text-zinc-800">Velaro Voice Setup</DialogTitle>
              <button
                onClick={onClose}
                aria-label="Close Velaro Voice setup"
                className="text-zinc-400 hover:text-zinc-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {showStepBar && <StepBar current={step} />}
          </div>

          <div className="flex-1 overflow-y-auto px-8 pb-2">
            {mode === "loading" && (
              <div className="py-10">
                <LoadingSpinner minHeight={200} />
              </div>
            )}

            {mode === "error" && (
              <div className="space-y-4 py-6 text-center">
                <div role="alert" className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {loadError}
                </div>
                <PrimaryButton onClick={() => fetchStatus()}>Retry</PrimaryButton>
              </div>
            )}

            {mode === "manage" && <ManageScreen />}

            {mode === "wizard" && step === "connect" && <StepConnect />}
            {mode === "wizard" && step === "verify" && <StepVerify />}
            {mode === "wizard" && step === "configure" && <StepConfigure />}
            {mode === "wizard" && step === "finish" && <StepFinish />}
          </div>

          {stepError && (
            <div role="alert" className="mx-8 mt-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {stepError}
            </div>
          )}

          {showFooter && (
            <>
              <Divider className="my-0" />
              <div className="px-8 py-6 flex items-center justify-between">
                <div>
                  {step !== "connect" ? (
                    <GhostButton onClick={goBack}>
                      <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
                    </GhostButton>
                  ) : (
                    <GhostButton onClick={onClose}>Cancel</GhostButton>
                  )}
                </div>
                <PrimaryButton onClick={goNext}>
                  <span className="flex items-center gap-1">
                    {step === "configure" ? "Activate" : "Next"} <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </PrimaryButton>
              </div>
            </>
          )}
        </ModalShell>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
